/**
 * Multi-profile storage for FlightSync.
 *
 * PRIMARY: localStorage (fast, synchronous)
 * BACKUP:  IndexedDB mirror - survives Android WebView cache eviction and
 *          partial storage clears that can happen on DJI RC after reboots.
 *          On read, if localStorage is empty we attempt to restore from IDB.
 *
 * localStorage keys:
 *  - fs_profiles    : [{ id, label, token, project_id, project_name, ... }]
 *  - fs_active      : id of the active profile
 *  - fs_ctx         : LEGACY single-profile context (migrated on first read)
 */

const KEY_PROFILES = 'fs_profiles';
const KEY_ACTIVE = 'fs_active';
const KEY_LEGACY = 'fs_ctx';

// --- IndexedDB mirror for resilience across reboots ---
const IDB_NAME = 'flightsync-profiles';
const IDB_STORE = 'kv';

function openProfilesDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(key, value) {
  return openProfilesDb().then(
    (db) => new Promise((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    }),
  ).catch(() => undefined);
}

function idbGetSync(key) {
  // Synchronous IDB isn't possible; we expose a sync API but kick an async
  // restore when localStorage is empty. Callers that need strict recovery use
  // restoreFromIdbAsync().
  return null; // unused placeholder for API symmetry
}

function idbGet(key) {
  return openProfilesDb().then(
    (db) => new Promise((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(undefined);
    }),
  ).catch(() => undefined);
}

function idbDel(key) {
  return openProfilesDb().then(
    (db) => new Promise((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    }),
  ).catch(() => undefined);
}

function mirrorToIdb() {
  idbPut(KEY_PROFILES, localStorage.getItem(KEY_PROFILES));
  idbPut(KEY_ACTIVE, localStorage.getItem(KEY_ACTIVE));
  idbPut(KEY_LEGACY, localStorage.getItem(KEY_LEGACY));
}

/**
 * Async: restore localStorage from IndexedDB if local is empty.
 * Call once on app mount. Returns the active profile (if any) after restore.
 */
export async function restoreFromIdbAsync() {
  const hasLocal = !!(localStorage.getItem(KEY_PROFILES) || localStorage.getItem(KEY_LEGACY));
  if (hasLocal) return getActiveProfile();
  const [p, a, l] = await Promise.all([idbGet(KEY_PROFILES), idbGet(KEY_ACTIVE), idbGet(KEY_LEGACY)]);
  if (p) localStorage.setItem(KEY_PROFILES, p);
  if (a) localStorage.setItem(KEY_ACTIVE, a);
  if (l) localStorage.setItem(KEY_LEGACY, l);
  return getActiveProfile();
}

function readRaw(key) {
  try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; }
}

function migrateLegacy() {
  const legacy = readRaw(KEY_LEGACY);
  if (!legacy || !legacy.token) return null;
  const profile = {
    id: `p_${Date.now()}`,
    label: legacy.project_name || 'Perfil principal',
    ...legacy,
  };
  localStorage.setItem(KEY_PROFILES, JSON.stringify([profile]));
  localStorage.setItem(KEY_ACTIVE, profile.id);
  localStorage.removeItem(KEY_LEGACY);
  mirrorToIdb();
  return profile;
}

export function listProfiles() {
  let profiles = readRaw(KEY_PROFILES);
  if (!profiles) {
    const migrated = migrateLegacy();
    profiles = migrated ? [migrated] : [];
  }
  return Array.isArray(profiles) ? profiles : [];
}

export function getActiveProfile() {
  const profiles = listProfiles();
  if (profiles.length === 0) return null;
  const activeId = localStorage.getItem(KEY_ACTIVE);
  return profiles.find((p) => p.id === activeId) || profiles[0];
}

export function addProfile(data) {
  const profiles = listProfiles();
  const profile = {
    id: `p_${Date.now()}`,
    label: data.project_name || data.pilot_name || 'Novo perfil',
    created_at: new Date().toISOString(),
    ...data,
  };
  profiles.push(profile);
  localStorage.setItem(KEY_PROFILES, JSON.stringify(profiles));
  localStorage.setItem(KEY_ACTIVE, profile.id);
  localStorage.setItem(KEY_LEGACY, JSON.stringify(profile));
  mirrorToIdb();
  return profile;
}

export function updateActiveProfile(patch) {
  const profiles = listProfiles();
  const active = getActiveProfile();
  if (!active) return null;
  const next = { ...active, ...patch };
  const idx = profiles.findIndex((p) => p.id === active.id);
  profiles[idx] = next;
  localStorage.setItem(KEY_PROFILES, JSON.stringify(profiles));
  localStorage.setItem(KEY_LEGACY, JSON.stringify(next));
  mirrorToIdb();
  return next;
}

export function switchProfile(id) {
  const profiles = listProfiles();
  const p = profiles.find((x) => x.id === id);
  if (!p) return null;
  localStorage.setItem(KEY_ACTIVE, id);
  localStorage.setItem(KEY_LEGACY, JSON.stringify(p));
  mirrorToIdb();
  return p;
}

export function removeProfile(id) {
  const profiles = listProfiles().filter((p) => p.id !== id);
  localStorage.setItem(KEY_PROFILES, JSON.stringify(profiles));
  const active = localStorage.getItem(KEY_ACTIVE);
  if (active === id) {
    if (profiles.length > 0) {
      localStorage.setItem(KEY_ACTIVE, profiles[0].id);
      localStorage.setItem(KEY_LEGACY, JSON.stringify(profiles[0]));
    } else {
      localStorage.removeItem(KEY_ACTIVE);
      localStorage.removeItem(KEY_LEGACY);
    }
  }
  mirrorToIdb();
  return profiles;
}

export function resetAll() {
  localStorage.removeItem(KEY_PROFILES);
  localStorage.removeItem(KEY_ACTIVE);
  localStorage.removeItem(KEY_LEGACY);
  idbDel(KEY_PROFILES);
  idbDel(KEY_ACTIVE);
  idbDel(KEY_LEGACY);
}
